import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    ViewChildren,
    QueryList,
} from '@angular/core';
import { SelectedFilterChipVm } from './selected-filter-chip.vm';

@Component({
    selector: 'app-selected-filters-chip-row',
    templateUrl: './selected-filters-chip-row.component.html',
    styleUrls: ['./selected-filters-chip-row.component.scss'],
})
export class SelectedFiltersChipRowComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input()
    categoryLabel = '';

    @Input()
    color = '';

    @Input()
    chips: SelectedFilterChipVm[] = [];

    @Input()
    removable = true;

    @Output()
    removeChip = new EventEmitter<SelectedFilterChipVm>();

    @ViewChild('chipTrack', { static: true })
    chipTrack: ElementRef<HTMLElement>;

    @ViewChildren('measureChip')
    measureChips: QueryList<ElementRef<HTMLElement>>;

    @ViewChild('moreWidthProbe', { read: ElementRef })
    moreWidthProbe: ElementRef<HTMLElement>;

    visibleCount = 0;
    showAllExpanded = false;
    /** Bumped when layout changes; not a template getter (avoids work every CD). */
    hiddenCount = 0;
    moreProbeCount = 1;

    private _chipWidths: number[] = [];
    private _moreProbeWidth = 72;
    private _ro: ResizeObserver;

    constructor(
        private _cdr: ChangeDetectorRef,
        private _zone: NgZone
    ) {}

    ngAfterViewInit(): void {
        this._ro = new ResizeObserver(() =>
            this._zone.run(() => {
                this._scheduleRecalc();
            })
        );
        this._ro.observe(this.chipTrack.nativeElement);
        this._scheduleRecalc();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.chips) {
            this.showAllExpanded = false;
            this.visibleCount = this.chips?.length ?? 0;
            this._syncMoreProbeCount();
            this._scheduleRecalc();
        }
    }

    ngOnDestroy(): void {
        this._ro?.disconnect();
    }

    toggleShowAll(): void {
        this.showAllExpanded = true;
        this.visibleCount = this.chips.length;
        this._syncHiddenCount();
        this._cdr.markForCheck();
    }

    collapseRow(): void {
        this.showAllExpanded = false;
        this._scheduleRecalc();
    }

    trackChip(_: number, chip: SelectedFilterChipVm): string {
        return `${chip.entityId}-${chip.entityType}-${chip.textFilterIndex ?? ''}-${chip.chipLabel}`;
    }

    private _scheduleRecalc(): void {
        requestAnimationFrame(() => this._recalcLayout());
    }

    private _recalcLayout(): void {
        if (!this.chipTrack?.nativeElement) {
            return;
        }

        if (this.showAllExpanded) {
            this.visibleCount = this.chips.length;
            this._syncHiddenCount();
            this._cdr.markForCheck();
            return;
        }

        this._cdr.detectChanges();

        requestAnimationFrame(() => {
            this._readChipWidths();
            this._applyVisibleCount();
            this._syncHiddenCount();
            this._cdr.markForCheck();
        });
    }

    private _readChipWidths(): void {
        const els = this.measureChips?.toArray() ?? [];
        this._chipWidths = els.map((r) => r.nativeElement.offsetWidth);
        if (this.moreWidthProbe?.nativeElement) {
            this._moreProbeWidth = Math.ceil(this.moreWidthProbe.nativeElement.offsetWidth) + 8;
        }
    }

    private _applyVisibleCount(): void {
        const nChips = this.chips.length;
        if (nChips === 0) {
            this.visibleCount = 0;
            return;
        }

        const gap = 6;
        const trackWidth = this.chipTrack.nativeElement.clientWidth;
        if (trackWidth <= 0) {
            this.visibleCount = nChips;
            return;
        }

        let bestN = 0;
        for (let n = nChips; n >= 1; n--) {
            let total = 0;
            for (let i = 0; i < n; i++) {
                const w = this._chipWidths[i] ?? 0;
                total += w;
                if (i < n - 1) {
                    total += gap;
                }
            }
            if (n < nChips) {
                total += gap + this._moreProbeWidth;
            }
            if (total <= trackWidth) {
                bestN = n;
                break;
            }
        }

        this.visibleCount = bestN > 0 ? bestN : 1;
    }

    private _syncMoreProbeCount(): void {
        const n = this.chips.length;
        this.moreProbeCount = n > 1 ? n - 1 : 1;
    }

    private _syncHiddenCount(): void {
        if (this.showAllExpanded || !this.chips.length) {
            this.hiddenCount = 0;
        } else {
            this.hiddenCount = Math.max(0, this.chips.length - this.visibleCount);
        }
    }
}
